import {
  ApplyChangesEnum,
  MidCycleBillingEnum,
  ScheduledChangesHandlingEnum,
  type SubscriptionItem,
} from '@bbkAdminRedux/rtkq/optimizations.slice';
import { NumberKit } from '@bbkAdminUtils/number-kit';

const formatItemType = (itemType: string): string => {
  if (!itemType) return '';
  return itemType.charAt(0).toUpperCase() + itemType.slice(1).toLowerCase();
};

const formatUnitPrice = (priceInCents: number): string => {
  const priceInDollars = priceInCents / 100;
  return NumberKit.formatMoneyWithCents(priceInDollars);
};

export type ScheduledChangeDetails = {
  changeType: 'Added' | 'Modified' | 'Removed';
  changeDetails: string;
};

export class OptimizationConfigHelper {
  // get details of scheduled changes (added, modified, removed) between current and scheduled subscription items
  static ScheduledChangesDetails = (
    currentItems: SubscriptionItem[],
    scheduledItems: SubscriptionItem[]
  ): ScheduledChangeDetails[] => {
    const changes: ScheduledChangeDetails[] = [];
    const currentItemIds = currentItems.map((item) => item.item_price_id);
    const scheduledItemIds = scheduledItems.map((item) => item.item_price_id);

    for (const scheduledItem of scheduledItems) {
      let changeDetails = `${formatItemType(scheduledItem.item_type)} ${scheduledItem.item_price_id} `;
      if (!currentItemIds.includes(scheduledItem.item_price_id)) {
        changes.push({
          changeType: 'Added',
          changeDetails: changeDetails + 'added',
        });
      } else {
        const currentItemIndex = currentItemIds.indexOf(
          scheduledItem.item_price_id
        );
        const currentItem = currentItems[currentItemIndex];
        if (!currentItem) {
          continue;
        }
        const modifications: string[] = [];

        const addModification = (
          field: string,
          current: number | undefined,
          scheduled: number | undefined,
          format: (v: number) => string = String
        ) => {
          if (
            current !== undefined &&
            scheduled !== undefined &&
            current !== scheduled
          ) {
            modifications.push(
              `${field} overridden (From ${format(current)} to ${format(scheduled)})`
            );
          }
        };

        addModification(
          'unit price',
          currentItem.unit_price,
          scheduledItem.unit_price,
          formatUnitPrice
        );
        addModification(
          'quantity',
          currentItem.quantity,
          scheduledItem.quantity
        );
        addModification(
          'billing period',
          currentItem.billing_period,
          scheduledItem.billing_period
        );
        addModification(
          'billing cycles',
          currentItem.billing_cycles,
          scheduledItem.billing_cycles
        );

        if (modifications.length > 0) {
          changes.push({
            changeType: 'Modified',
            changeDetails: changeDetails + modifications.join(', '),
          });
        }
      }
    }
    for (const currentItem of currentItems) {
      if (!scheduledItemIds.includes(currentItem.item_price_id)) {
        changes.push({
          changeType: 'Removed',
          changeDetails: `${formatItemType(currentItem.item_type)} ${currentItem.item_price_id} removed`,
        });
      }
    }
    return changes;
  };

  // Get label for the apply changes option for overview display in Optimization Review
  static getApplyChangesLabel = (value: ApplyChangesEnum) => {
    switch (value) {
      case ApplyChangesEnum.IMMEDIATELY:
        return 'Apply immediately';
      case ApplyChangesEnum.END_OF_TERM:
        return 'Apply at renewal (scheduled)';
      default:
        return 'Not supported';
    }
  };

  // Get apply changes options for radio groups on configurations screen
  static getApplyChangesOptions = () => [
    {
      value: ApplyChangesEnum.IMMEDIATELY,
      label: 'Apply immediately',
      description:
        'Changes are applied to all target subscriptions immediately.',
    },
    {
      value: ApplyChangesEnum.END_OF_TERM,
      label: 'Apply at renewal',
      description:
        'Changes are written as scheduled changes on each subscription. They will take effect when the subscription next renews.',
    },
  ];

  // Get label for the proration option for overview display in Optimization Review
  static getProrationLabel = (value: MidCycleBillingEnum | undefined) => {
    switch (value) {
      case 'disable_proration':
        return 'Proration Disabled';
      case 'enable_proration':
        return 'Proration Enabled';
      default:
        return 'Proration Option Not Selected';
    }
  };

  // Get proration options for radio groups on configurations screen
  static getProrationOptions = () => {
    return (Object.values(MidCycleBillingEnum) as MidCycleBillingEnum[]).map(
      (value) => {
        switch (value) {
          case 'disable_proration':
            return {
              value,
              label: 'Disable Proration',
              description:
                'Recommended. No mid-cycle charges, customers pay the new amount starting from their next billing cycle.',
            };
          case 'enable_proration':
            return {
              value,
              label: 'Enable Proration',
              description:
                'Generate immediate charges or credits for the price difference from today until their next billing cycle.',
            };
          default:
            return {
              value,
              label: 'Proration Option Not Selected',
              description: '',
            };
        }
      }
    );
  };

  // Get label for the scheduled changes handling option for overview display in Optimization Review
  static getChangeHandlingLabel = (
    value: ScheduledChangesHandlingEnum | undefined
  ) => {
    switch (value) {
      case 'skip_conflicting_subscribers':
        return 'Skip Conflicting Subscribers';
      case 'discard_existing_schedules':
        return 'Discard Existing Schedules';
      case 'retain_existing_schedules':
        return 'Retain Existing Scheduled Changes With New Rules';
      default:
        return 'Scheduled Change Handling Option Not Selected';
    }
  };

  // Get scheduled change handling options for radio groups on configurations screen
  static getChangeHandlingOptions = () => {
    return (
      Object.values(
        ScheduledChangesHandlingEnum
      ) as ScheduledChangesHandlingEnum[]
    ).map((value) => {
      switch (value) {
        case 'skip_conflicting_subscribers':
          return {
            value,
            label: OptimizationConfigHelper.getChangeHandlingLabel(value),
            description:
              "Don't apply new changes to subscribers who already have future changes scheduled. Create a separate optimization for them later.",
          };
        case 'discard_existing_schedules':
          return {
            value,
            label: OptimizationConfigHelper.getChangeHandlingLabel(value),
            description:
              'Cancel their existing scheduled changes and apply your new changes instead. This cannot be undone.',
          };
        case 'retain_existing_schedules':
          return {
            value,
            label: OptimizationConfigHelper.getChangeHandlingLabel(value),
            description:
              'Keep their existing scheduled changes and add your new changes. Both will activate as planned.',
          };
        default:
          return {
            value,
            label: 'Proration Option Not Selected',
            description: '',
          };
      }
    });
  };

  // Get added, modified, or removed badge variant for scheduled change
  static getSubscriptionChangeBadgeVariant = (
    type: 'Added' | 'Removed' | 'Modified'
  ) => {
    switch (type) {
      case 'Added':
        return 'success';
      case 'Removed':
        return 'danger';
      case 'Modified':
        return 'warning';
      default:
        return 'warning';
    }
  };
}
